import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { WS_EVENTS } from '@twinden/shared';
import { api } from '../lib/api';
import { familiesAtom, joinRequestCountsAtom } from '../store';
import { useWebSocket } from './useWebSocket';

type CountPayload = { familyId?: string; pending?: number };

// Keeps the "people asking to join" badge (D154) for every family where I'm
// a root or admin. Mounted once, in the app shell; the server's family
// events carry the new pending count, so no refetch is needed for them.
export function useJoinRequestCounts() {
  const families = useAtomValue(familiesAtom);
  const [counts, setCounts] = useAtom(joinRequestCountsAtom);
  const { onEvent, connectionEpoch } = useWebSocket();

  const approverOf = useMemo(
    () =>
      families
        .filter((family) => family.role === 'root' || family.role === 'admin')
        .map((family) => family.id),
    [families]
  );
  const approverKey = approverOf.join(',');

  const refresh = useCallback(async () => {
    const ids = approverKey ? approverKey.split(',') : [];
    const results = await Promise.allSettled(
      ids.map((id) => api.getJoinRequests(id))
    );
    const next: Record<string, number> = {};
    results.forEach((result, index) => {
      if (result.status === 'fulfilled')
        next[ids[index]!] = result.value.data.requests.length;
    });
    setCounts(next);
  }, [approverKey, setCounts]);

  // Again after a reconnect, in case events were missed while offline.
  useEffect(() => {
    void refresh();
  }, [refresh, connectionEpoch]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (
          event !== WS_EVENTS.JOIN_REQUEST_CREATED &&
          event !== WS_EVENTS.JOIN_REQUEST_DECIDED
        )
          return;
        const { familyId, pending } = payload as CountPayload;
        if (!familyId || typeof pending !== 'number') return;
        // The requester gets DECIDED too; only approvers keep a count.
        if (!approverKey.split(',').includes(familyId)) return;
        setCounts((current) => ({ ...current, [familyId]: pending }));
      }),
    [onEvent, approverKey, setCounts]
  );

  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  return { counts, total, refresh };
}
